// Adapter: transforms the flat DB schema (title_ar, title_en, ...) into the
// legacy nested shape ({ title: { ar, en } }) so Work.jsx and ProjectDetail.jsx
// can render without deep changes.
import { projects as fallbackProjectsRaw, categories as staticCategories } from "@/data/projects";

const langMap = (obj, base) => ({
  ar: obj[`${base}_ar`] || "",
  en: obj[`${base}_en`] || "",
});

// Convert any YouTube/Vimeo URL to an embeddable iframe URL.
// Handles: watch?v=, youtu.be/, shorts/, live/, already-embed, and Vimeo.
export const toEmbedUrl = (url) => {
  if (!url || typeof url !== "string") return "";
  const raw = url.trim();
  if (!raw) return "";

  // Already an embed
  if (/youtube(-nocookie)?\.com\/embed\//i.test(raw)) return raw;
  if (/player\.vimeo\.com\/video\//i.test(raw)) return raw;

  // YouTube — extract 11-char video id from any known form
  const ytPatterns = [
    /youtu\.be\/([A-Za-z0-9_-]{11})/i,
    /youtube\.com\/watch\?(?:[^"']*&)?v=([A-Za-z0-9_-]{11})/i,
    /youtube\.com\/shorts\/([A-Za-z0-9_-]{11})/i,
    /youtube\.com\/live\/([A-Za-z0-9_-]{11})/i,
    /youtube\.com\/v\/([A-Za-z0-9_-]{11})/i,
  ];
  for (const re of ytPatterns) {
    const m = raw.match(re);
    if (m && m[1]) return `https://www.youtube.com/embed/${m[1]}`;
  }

  // Vimeo — /video-id or /vimeo.com/{id}
  const vimeo = raw.match(/vimeo\.com\/(?:video\/)?(\d+)/i);
  if (vimeo && vimeo[1]) return `https://player.vimeo.com/video/${vimeo[1]}`;

  // Unknown — return as-is (may be a direct MP4 or a self-hosted stream)
  return raw;
};

export const adaptProject = (raw) => {
  if (!raw) return null;
  const paragraphsAr = (raw.vision_paragraphs_ar || "")
    .split(/\n\s*\n/).map((s) => s.trim()).filter(Boolean);
  const paragraphsEn = (raw.vision_paragraphs_en || "")
    .split(/\n\s*\n/).map((s) => s.trim()).filter(Boolean);

  const hasVision = raw.hero_layout === "vision"
    || paragraphsAr.length > 0
    || paragraphsEn.length > 0
    || !!raw.vision_title_ar
    || !!raw.vision_title_en;

  return {
    id: raw.id,
    slug: raw.slug,
    image: raw.image || "",
    year: raw.year || "",
    category: raw.category || "Commercial",
    title: langMap(raw, "title"),
    short: langMap(raw, "short"),
    client: langMap(raw, "client"),
    goal: langMap(raw, "goal"),
    timeline: langMap(raw, "timeline"),
    approachHeadline: langMap(raw, "approach_headline"),
    approach: langMap(raw, "approach"),
    locations: langMap(raw, "locations"),
    team: langMap(raw, "team"),
    result: langMap(raw, "result"),
    body: (raw.body_ar || raw.body_en) ? langMap(raw, "body") : undefined,
    equipment: raw.equipment || [],
    tools: raw.tools || [],
    hideMeta: !!raw.hide_meta,
    hideApproach: !!raw.hide_approach,
    hideHighlights: !!raw.hide_highlights,
    hideResult: !!raw.hide_result,
    fullWidthVideo: !!raw.full_width_video,
    heroLayout: raw.hero_layout || "",
    resultBackground: raw.result_background || "",
    externalUrl: raw.external_url || "",
    video: toEmbedUrl(raw.video || ""),
    vision: hasVision
      ? {
          title: {
            ar: raw.vision_title_ar || "",
            en: raw.vision_title_en || "",
          },
          paragraphs: {
            ar: paragraphsAr,
            en: paragraphsEn,
          },
        }
      : undefined,
  };
};

export const adaptProjects = (list) => (list || []).map(adaptProject).filter(Boolean);

// Also normalize the static fallback (in case those get shown before API loads)
const fallbackProjects = (fallbackProjectsRaw || []).map((p) => ({
  ...p,
  video: toEmbedUrl(p.video || ""),
}));

export { staticCategories as categories, fallbackProjects };

